/**
 * Hardcoded "Built-in capabilities · ask in natural language" section of the
 * ``/?`` discovery listing, for channels whose capability set the backend
 * does not yet project (see ``capability_listing.py``'s ``BUILT_IN_GROUPS``,
 * which currently only covers ``word``): Outlook and PowerPoint.
 *
 * Skills stay entirely dynamic — persona-scoped, fetched from the existing
 * ``GET /api/v1/skills/capabilities/{channel}`` /
 * ``GET /api/v1/skills/slash-commands/{channel}`` endpoints, unchanged by this
 * module. What is hardcoded here is only the fixed, persona-independent list
 * of things the add-in itself can always do on a channel — there is nothing
 * to fetch for that, and the Outlook feature specification (§4.2) is explicit
 * that every one of these entries is "deliberately not /-firable": the
 * listing exists purely to teach the user that plain language works.
 *
 * Consumed by ``useChat.ts``'s discovery-listing renderers, which merge this
 * in only when the fetched listing carries no ``builtin_``-prefixed group
 * already (i.e. only for channels the backend hasn't taken over) — so a
 * channel added here has no effect once the backend starts supplying its own
 * built-in groups, and nothing here duplicates or fights the server-owned
 * Word taxonomy.
 */
import type { Channel } from "../../shared/host";
import type { CapabilityGroupDTO } from "../services/api";

interface BuiltInCapabilityGroup {
  readonly key: string;
  readonly title: string;
  readonly items: readonly string[];
}

// Outlook feature specification, §4.2, Figure 4.1 — verbatim group titles and
// bullet text, in display order.
//
// ONE deliberate departure from verbatim: Figure 4.1 words the third mailbox
// entry "Surface unresolved actions in the inbox", which is the one thing MS-03's
// own boundary forbids the product from saying — "it is not an inbox-wide sweep …
// copy must not imply otherwise". The two passages contradict each other, and the
// boundary is the one stating intent, so the promise is scoped to what the
// capability does. Changing the behaviour to match the figure is not an option:
// there is no standing index of the mailbox to sweep (AIVA-7017).
const OUTLOOK_BUILT_INS: readonly BuiltInCapabilityGroup[] = Object.freeze([
  {
    key: "builtin_drafting",
    title: "Drafting & Replying",
    items: [
      "Draft a reply to the open email",
      "Compose a new email from a brief",
      "Adjust tone or formality of a draft",
      "Suggest follow-ups or next steps",
    ],
  },
  {
    key: "builtin_mailbox",
    title: "Mailbox Search & Triage",
    items: [
      "Find emails by sender, subject or content",
      "Summarise long email threads",
      "Surface unresolved actions in the open conversation",
    ],
  },
  {
    key: "builtin_calendar",
    title: "Calendar & Meetings",
    items: [
      "Find a free slot and book a meeting",
      "Generate a meeting agenda",
      "Convert email content into calendar events",
    ],
  },
  {
    key: "builtin_tasks",
    title: "Tasks & Follow-ups",
    items: ["Extract action items from emails", "Create tasks from email content"],
  },
]);

// ── PowerPoint (AIVA-6804) ───────────────────────────────────────────────────
//
// Hardcoded here, like Outlook, rather than server-owned like Word, for two
// reasons the PowerPoint specification forces:
//  - G-12: the Slide Design group appears only when the Deckorate module is
//    enabled, and only the add-in knows that;
//  - ST-03: the listing must keep working with the AI service offline, and the
//    capabilities endpoint lives on that same service.
//
// Entries follow the interactive mockup's PowerPoint listing, which matches
// §4.2 Figure 4.1's entries verbatim, minus "Find / replace across all slides":
// DI-03 is not being built (AIVA-8913), so the listing must not promise it.
// Group titles are plain text, like Word's and Outlook's — the mockup's icons
// are not carried over.

const POWERPOINT_BUILT_INS: readonly BuiltInCapabilityGroup[] = Object.freeze([
  {
    key: "builtin_slide_content",
    title: "Slide Content",
    items: [
      "Draft slide content from a brief",
      "Insert generated text into the current slide",
      "Replace selected shape text",
      "Add content as speaker notes",
      "Create a new slide with the response",
      // SC-06 (AIVA-6810). Not in Figure 4.1, which predates it; listed so its
      // /rephrase command is discoverable here (AIVA-8971).
      "Rephrase the text on the current slide",
    ],
  },
  {
    key: "builtin_deck_intelligence",
    title: "Deck Intelligence",
    items: [
      "Summarise the entire deck",
      "List speaker notes across slides",
      "Show what's on the current slide",
    ],
  },
]);

/** G-12 — listed only when the Deckorate module is enabled for the user. */
const POWERPOINT_SLIDE_DESIGN_GROUP: BuiltInCapabilityGroup = Object.freeze({
  key: "builtin_slide_design",
  title: "Slide Design (via Deckorate)",
  items: [
    "Apply a themed layout",
    "Brand colours and fonts",
    "Transform or redesign a slide",
    "Convert a table to a chart",
  ],
});

/**
 * AIVA-8971 — the slash command behind a PowerPoint built-in, shown beside it
 * when the user has that command. "Insert generated text…" has none: it is the
 * Insert into slide button under a /draft reply, not a request of its own.
 */
const POWERPOINT_BUILT_IN_COMMANDS: Readonly<Record<string, string>> = Object.freeze({
  "Draft slide content from a brief": "/draft",
  "Replace selected shape text": "/rewrite",
  "Add content as speaker notes": "/notes",
  "Create a new slide with the response": "/newslide",
  "Rephrase the text on the current slide": "/rephrase",
  "Summarise the entire deck": "/summarise",
  "List speaker notes across slides": "/shownotes",
  "Show what's on the current slide": "/slide",
});

const BUILT_IN_COMMANDS: Partial<Record<Channel, Readonly<Record<string, string>>>> = Object.freeze({
  powerpoint: POWERPOINT_BUILT_IN_COMMANDS,
});

/** Options that change which built-ins a channel lists. */
export interface BuiltInGroupOptions {
  /** PowerPoint only: the Deckorate module is enabled for this user (G-12). */
  readonly deckorateEnabled?: boolean;
  /**
   * The slashes this user can run on the channel (AIVA-8971). A built-in whose
   * command is among them is listed with it; without this, none are.
   */
  readonly availableSlashes?: ReadonlySet<string>;
}

function powerPointBuiltIns(options: BuiltInGroupOptions): readonly BuiltInCapabilityGroup[] {
  return options.deckorateEnabled ? [...POWERPOINT_BUILT_INS, POWERPOINT_SLIDE_DESIGN_GROUP] : POWERPOINT_BUILT_INS;
}

// Only channels the backend does not already project built-ins for. Word is
// deliberately absent — it is server-owned (see the module docstring).
const HARDCODED_BUILT_INS: Partial<
  Record<Channel, (options: BuiltInGroupOptions) => readonly BuiltInCapabilityGroup[]>
> = Object.freeze({
  outlook: () => OUTLOOK_BUILT_INS,
  powerpoint: powerPointBuiltIns,
});

/**
 * The hardcoded built-in capability groups for ``channel``, shaped as
 * ``CapabilityGroupDTO[]`` so callers can splice them straight into a fetched
 * (or locally built) listing's ``groups`` array. An item's ``slash`` is ``""``
 * — asked for in plain language — unless the channel maps it to a command
 * the user has (``options.availableSlashes``, AIVA-8971); then it carries that
 * command without its leading "/", as the backend's items do.
 *
 * Returns ``[]`` for a channel with no hardcoded entry (Word, or an
 * unrecognised value) rather than throwing — an unmapped channel should
 * degrade to "skills only", not break ``/?`` for everyone on it.
 */
export function getHardcodedBuiltInGroups(
  channel: string | null,
  options: BuiltInGroupOptions = {}
): readonly CapabilityGroupDTO[] {
  const build = channel ? HARDCODED_BUILT_INS[channel as Channel] : undefined;
  if (!build) return [];
  const commands = BUILT_IN_COMMANDS[channel as Channel] ?? {};
  const slashFor = (text: string): string => {
    const command = commands[text];
    return command && options.availableSlashes?.has(command) ? command.slice(1) : "";
  };
  return build(options).map((group) => ({
    key: group.key,
    title: group.title,
    items: group.items.map((text) => ({ slash: slashFor(text), label: text, description: text })),
  }));
}
